// src/pages/ClinicalNotes/ClinicalNotesList.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import "./ClinicalNotesList.css";
import { getEhrNotes } from "../../utils/services/AdvancedMD/ehrNotesQuery";
import NotesFilters from "./components/NotesFilters";
import NotesTable from "./components/NotesTable";

// 👉 MUI
import { Button, Snackbar, Alert } from "@mui/material";

// -------- Helpers -----------------------------------------------------
function mapFields(fields = []) {
  const m = {};
  fields.forEach((f) => (m[f?.name] = f?.value ?? ""));
  return m;
}
function firstFieldsArray(note) {
  return note?.pages?.[0]?.fields ?? [];
}
const getFrom = (fieldsMap) => (k, fallback = "—") =>
  (fieldsMap[k] ?? "").toString().trim() || fallback;

// Simple mini-card for single values
function FieldCard({ label, value, icon }) {
  return (
    <div className="mini-card">
      <div className="mini-card-top">
        <span className="mini-card-icon" aria-hidden>
          {icon || "•"}
        </span>
        <span className="mini-card-label">{label}</span>
      </div>
      <div className="mini-card-value">{value || "—"}</div>
    </div>
  );
}

// Section wrapper with title
function SectionCard({ title, icon, children }) {
  return (
    <div className="cnl-box">
      <div className="cnl-box-title">
        <span className="cnl-box-icon" aria-hidden>
          {icon}
        </span>
        <h4>{title}</h4>
      </div>
      {children}
    </div>
  );
}

export default function ClinicalNotesList() {
  const [patientId, setPatientId] = useState("129807");
  const [templateId, setTemplateId] = useState("");

  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [count, setCount] = useState(0);
  const [error, setError] = useState("");

  // Inline detail
  const [selectedNote, setSelectedNote] = useState(null);

  // 👉 Control del aviso de permisos
  const [denyOpen, setDenyOpen] = useState(false);
  const handleCreateClick = () => setDenyOpen(true);
  const handleDenyClose = (_e, reason) => {
    if (reason === "clickaway") return;
    setDenyOpen(false);
  };

  const loadNotes = useCallback(async () => {
    const pid = (patientId || "").trim();
    if (!pid) return;

    setLoading(true);
    setError("");
    setSelectedNote(null);
    try {
      const res = await getEhrNotes({
        patientId: pid,
        templateId: (templateId || "").trim() || undefined,
      });
      setNotes(res?.notes ?? []);
      setCount(res?.count ?? 0);
    } catch (e) {
      console.error("loadNotes error:", e);
      setNotes([]);
      setCount(0);
      setError("Unable to load clinical notes. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [patientId, templateId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleView = (note) => {
    if (!note?.id) return;
    setSelectedNote(note);
    setTimeout(() => {
      const el = document.getElementById("cnl-detail");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  // Detail memo
  const fieldsMap = useMemo(
    () => mapFields(firstFieldsArray(selectedNote)),
    [selectedNote]
  );
  const get = useMemo(() => getFrom(fieldsMap), [fieldsMap]);

  return (
    <div className="cnl-container">
      <div className="cnl-header">
        {/* Fila de título + botón */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <h2 style={{ margin: 0 }}>Clinical Notes</h2>
          <Button
            variant="contained"
            onClick={handleCreateClick}
            aria-label="Create clinical note"
          >
            Create clinical note
          </Button>
        </div>

        {/* Filtros */}
        <NotesFilters
          patientId={patientId}
          templateId={templateId}
          onChangePatientId={setPatientId}
          onChangeTemplateId={setTemplateId}
          onSearch={loadNotes}
          loading={loading}
        />
      </div>

      {error && <div className="cnl-error">{error}</div>}

      <div className="cnl-card">
        <div className="cnl-summary">
          {loading ? "Loading…" : `Total notes: ${count}`}
        </div>

        {!loading && !error && notes.length === 0 ? (
          <div className="cnl-empty">No notes to display.</div>
        ) : (
          <NotesTable notes={notes} loading={loading} onView={handleView} />
        )}
      </div>

      {/* ============= Inline Detail ============= */}
      {selectedNote && (
        <div id="cnl-detail" className="cnl-card cnl-detail">
          <div className="cnl-detail-header">
            <div>
              <h3>🗒️ Note #{selectedNote.id}</h3>
              <div className="cnl-detail-sub">
                <span>📄 Template: {selectedNote.templateName}</span>
                <span>🕒 {selectedNote.noteDateTime}</span>
                <span>👤 {selectedNote.userName || "—"}</span>
                <span>💬 {selectedNote.comments || "—"}</span>
              </div>
            </div>
            <button
              className="cnl-btn ghost"
              onClick={() => setSelectedNote(null)}
              title="Close detail"
            >
              ✖
            </button>
          </div>

          {/* Summary sections with mini-cards */}
          <div className="cnl-grid3">
            <SectionCard title="Location" icon="📍">
              <div className="mini-card-grid">
                <FieldCard label="Location" value={get("location")} icon="🏥" />
                <FieldCard label="Location 2" value={get("location2")} icon="📌" />
                <FieldCard label="Room" value={get("location1")} icon="🚪" />
                <FieldCard
                  label="Reason"
                  value={get("ReasonforAppointment")}
                  icon="📝"
                />
              </div>
            </SectionCard>

            <SectionCard title="Vitals" icon="🫀">
              <div className="mini-card-grid">
                <FieldCard label="Weight" value={get("Vitals_Weight")} icon="⚖️" />
                <FieldCard label="Height" value={get("Vitals_Height")} icon="📏" />
                <FieldCard label="BMI" value={get("Vitals_BMI")} icon="📊" />
                <FieldCard
                  label="Temperature"
                  value={get("Vitals_Temperature")}
                  icon="🌡️"
                />
                <FieldCard label="Pulse" value={get("Vitals_Pulse")} icon="❤️" />
                <FieldCard label="O₂ Sat (RA)" value={get("Vitals_O2SatRA")} icon="🫁" />
                <FieldCard
                  label="BP Previous"
                  value={`${get("Vitals_BP_SystolicPrev")}/${get(
                    "Vitals_BP_DiastolicPrev"
                  )}`}
                  icon="🩺"
                />
                <FieldCard
                  label="BP #1"
                  value={`${get("Vitals_BP_Systolic1")}/${get(
                    "Vitals_BP_Diastolic1"
                  )}`}
                  icon="🩺"
                />
              </div>
            </SectionCard>

            <SectionCard title="HPI & History" icon="🧾">
              <div className="mini-card-grid">
                <FieldCard
                  label="HPI"
                  value={get("HistoryofPresentIllness")}
                  icon="🗒️"
                />
                <FieldCard
                  label="TCM"
                  value={get("HistoryofPresentIllness1")}
                  icon="🔁"
                />
                <FieldCard
                  label="Meds reconciled"
                  value={get("Allmedicationsreviewedandreconciled")}
                  icon="✅"
                />
                <FieldCard
                  label="Current meds"
                  value={get("CurrentMedications")}
                  icon="💊"
                />
                <FieldCard label="PMH flag" value={get("PMH")} icon="🏷️" />
                <FieldCard label="PMH details" value={get("PMH2")} icon="📚" />
              </div>
            </SectionCard>
          </div>

          {/* ROS */}
          <SectionCard title="ROS" icon="🧩">
            <div className="mini-card-grid">
              {[
                ["Constitutional", "Constitutional", "🧍"],
                ["Ophthalmologic", "Ophthalmologic", "👁️"],
                ["ENT", "ENT", "👂"],
                ["Endocrine", "Endocrine", "🧬"],
                ["Respiratory", "Respiratory", "🫁"],
                ["Cardiovascular", "Cardiovascular", "❤️"],
                ["Gastrointestinal", "Gastrointestinal", "🧠"], // placeholder
                ["Genitourinary", "Genitourinary", "🚻"],
                ["Musculoskeletal1", "Musculoskeletal", "🦴"],
                ["Neurologic1", "Neurologic", "🧠"],
                ["Psy", "Psych", "🧠"],
              ].map(([key, label, icon]) => (
                <FieldCard key={key} label={label} value={get(key)} icon={icon} />
              ))}
            </div>
          </SectionCard>

          {/* Physical Exam */}
          <SectionCard title="Physical Exam" icon="🩺">
            <div className="mini-card-grid">
              {[
                ["GeneralAppearance", "General", "🙂"],
                ["EyesBL", "Eyes", "👀"],
                ["Ears", "Ears", "👂"],
                ["Nose", "Nose", "👃"],
                ["OralCavity", "Oral cavity", "👄"],
                ["Throat", "Throat", "🗣️"],
                ["NeckThyroid", "Neck/Thyroid", "🧈"],
                ["Heart", "Heart", "❤️"],
                ["Lungs", "Lungs", "🫁"],
                ["Abdomen", "Abdomen", "🦴"],
                ["back", "Back", "🧍"],
                ["Musculoskeletal", "MSK", "🦴"],
                ["Skin", "Skin", "🧴"],
                ["Neurologic", "Neuro", "🧠"],
                ["Extremities", "Extremities", "🦵"],
                ["Psychiatric", "Psych", "🧠"],
              ].map(([key, label, icon]) => (
                <FieldCard key={key} label={label} value={get(key)} icon={icon} />
              ))}
            </div>
          </SectionCard>

          {/* Plan */}
          <div className="cnl-grid3">
            <SectionCard title="Assessments" icon="🧠">
              <div className="mini-card large">
                <div className="mini-card-top">
                  <span className="mini-card-icon">🧠</span>
                  <span className="mini-card-label">Assessments</span>
                </div>
                <pre className="pre">{get("Assessments")}</pre>
              </div>
            </SectionCard>

            <SectionCard title="Treatment" icon="💊">
              <div className="mini-card large">
                <div className="mini-card-top">
                  <span className="mini-card-icon">💊</span>
                  <span className="mini-card-label">Treatment</span>
                </div>
                <pre className="pre">{get("Treatment")}</pre>
              </div>
            </SectionCard>

            <SectionCard title="Follow up" icon="📆">
              <div className="mini-card large">
                <div className="mini-card-top">
                  <span className="mini-card-icon">📆</span>
                  <span className="mini-card-label">Follow up</span>
                </div>
                <pre className="pre">{get("FollowUp")}</pre>
              </div>
            </SectionCard>
          </div>

          <div className="cnl-detail-footer">
            <a
              href={`/create-clinical-note?noteId=${selectedNote.id}`}
              className="cnl-btn"
            >
              Edit in CreateClinicalNote
            </a>
          </div>
        </div>
      )}

      {/* Snackbar de permisos */}
      <Snackbar
        open={denyOpen}
        autoHideDuration={4000}
        onClose={handleDenyClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleDenyClose} severity="warning" variant="filled" sx={{ width: "100%" }}>
          You do not have permission to create clinical notes.
        </Alert>
      </Snackbar>
    </div>
  );
}
